import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import { auth, db } from './Firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useCompetitions } from './CompetitionContext';
import { getArchiveSeasons, leaguePath } from '../utils/siteUrls';
import logo from '../assets/images/logo.webp';

const EUROPEAN_CUPS = [
  { competitionId: 836, label: 'Liga Mistrzów' },
  { competitionId: 837, label: 'Liga Europy' },
  { competitionId: 838, label: 'Liga Konferencji', name: 'Liga Konferencji Europy' },
];

const topLevelItems = [
  { label: 'Start', path: '/', icon: HomeRoundedIcon },
  { label: 'Ranking Elo', path: '/elo-ranking', icon: EmojiEventsRoundedIcon },
  { label: 'Wybryki statystyczne', path: '/achievements-list', icon: InsightsRoundedIcon },
  { label: 'Wykresy', path: '/team/1/analytics?empty=1', icon: BarChartRoundedIcon },
  { label: 'Analiza kursów', path: '/odds', icon: PaidRoundedIcon },
  { label: 'Artykuły', path: '/blog', icon: ArticleRoundedIcon },
];

const hasGroup = (group) => {
  const value = (group || '').toString().trim();
  return value && value !== 'Brak' && value.toLowerCase() !== 'null';
};

const competitionDisplayName = (competition) =>
  hasGroup(competition.group) ? `${competition.name} (${competition.group.trim()})` : competition.name;

const competitionUrl = (competition) =>
  leaguePath(competition.competition_id, competition);

const compactGroupLabel = (competition) => {
  if (!hasGroup(competition.group)) return competition.name;
  return competition.group.charAt(0).toUpperCase() + competition.group.slice(1);
};

const getLatestCompetitions = (competitions, activeSeason) =>
  Object.values(competitions || {})
    .flat()
    .filter((competition) => competition.season === activeSeason && competition.country === 'Poland');

const buildCompetitionSections = (competitions, activeSeason) => {
  const allCompetitions = Object.values(competitions || {}).flat();
  const latestPolish = getLatestCompetitions(competitions, activeSeason)
    .filter((competition) => competition.type === 'league');

  const europeanCupLinks = EUROPEAN_CUPS.map((cup) => {
    const competition = allCompetitions.find((item) => Number(item.competition_id) === cup.competitionId);
    return {
      label: competition?.name || cup.label,
      path: competition ? competitionUrl(competition) : leaguePath(cup.competitionId, { name: cup.name || cup.label, group: 'faza ligowa' }),
    };
  });

  const central = latestPolish
    .filter((competition) => Number(competition.level) >= 1 && Number(competition.level) <= 4)
    .sort((a, b) => Number(a.level) - Number(b.level) || (a.group || '').localeCompare(b.group || '', 'pl'));

  const centralSections = Object.values(
    central.reduce((acc, competition) => {
      const key = `${competition.level}-${competition.name}`;
      if (!acc[key]) {
        acc[key] = {
          label: competition.name,
          children: [],
        };
      }
      acc[key].children.push({
        label: central.filter((item) => item.name === competition.name).length > 1
          ? compactGroupLabel(competition)
          : competition.name,
        path: competitionUrl(competition),
      });
      return acc;
    }, {})
  );

  const regional = latestPolish
    .filter((competition) => Number(competition.level) > 4)
    .sort((a, b) =>
      (a.region || '').localeCompare(b.region || '', 'pl') ||
      Number(a.level) - Number(b.level) ||
      (a.group || '').localeCompare(b.group || '', 'pl')
    );

  const regionalSections = Object.values(
    regional.reduce((acc, competition) => {
      const key = competition.region_id || competition.region || 'regionalne';
      if (!acc[key]) {
        acc[key] = {
          label: competition.region || `Region ${competition.region_id}`,
          children: [],
        };
      }
      acc[key].children.push({
        label: competitionDisplayName(competition),
        path: competitionUrl(competition),
      });
      return acc;
    }, {})
  );

  const nationsLeagueSource = allCompetitions.filter((competition) => competition.name === 'Liga Narodów');
  const activeNationsLeague = nationsLeagueSource.filter((competition) => competition.season === activeSeason);
  const nationsLeagueLinks = (activeNationsLeague.length ? activeNationsLeague : nationsLeagueSource)
    .slice()
    .sort((a, b) =>
      compactGroupLabel(a).localeCompare(compactGroupLabel(b), 'pl') ||
      competitionDisplayName(a).localeCompare(competitionDisplayName(b), 'pl')
    )
    .map((competition) => ({
      label: hasGroup(competition.group) ? compactGroupLabel(competition) : competitionDisplayName(competition),
      path: competitionUrl(competition),
    }));

  const archiveSections = getArchiveSeasons(competitions, activeSeason).map((season) => {
    const seasonCompetitions = Object.values(competitions || {})
      .flat()
      .filter((competition) =>
        competition.season === season &&
        competition.country === 'Poland' &&
        competition.type === 'league'
      )
      .sort((a, b) => Number(a.level) - Number(b.level) || (a.region || '').localeCompare(b.region || '', 'pl'));

    const centralArchive = seasonCompetitions.filter((competition) => {
      const level = Number(competition.level);
      return level >= 1 && level <= 4;
    });
    const regionalArchive = seasonCompetitions.filter((competition) => Number(competition.level) > 4);

    const centralByName = Object.values(centralArchive.reduce((acc, competition) => {
      const key = `${competition.level}-${competition.name}`;
      if (!acc[key]) {
        acc[key] = {
          label: competition.name,
          children: [],
        };
      }
      acc[key].children.push({
        label: centralArchive.filter((item) => item.name === competition.name).length > 1
          ? compactGroupLabel(competition)
          : competition.name,
        path: competitionUrl(competition),
      });
      return acc;
    }, {}));

    const centralArchiveSections = centralByName.map((section) => (
      section.children.length === 1
        ? { label: section.label, path: section.children[0].path }
        : section
    ));

    const regionalByRegion = Object.values(regionalArchive.reduce((acc, competition) => {
      const key = competition.region_id || competition.region || 'regionalne';
      if (!acc[key]) {
        acc[key] = {
          label: competition.region || `Region ${competition.region_id}`,
          children: [],
        };
      }
      acc[key].children.push({
        label: competitionDisplayName(competition),
        path: competitionUrl(competition),
      });
      return acc;
    }, {}));

    return {
      label: season,
      children: [
        ...centralArchiveSections,
        ...(regionalByRegion.length ? [{ label: 'Regionalne', children: regionalByRegion }] : []),
      ],
    };
  }).filter((section) => section.children.length > 0);

  return [
    { label: 'Europejskie puchary', children: europeanCupLinks },
    ...centralSections.map((section) => (
      section.children.length === 1
        ? { label: section.label, path: section.children[0].path }
        : section
    )),
    ...(regionalSections.length ? [{ label: 'Regionalne', children: regionalSections }] : []),
    ...(nationsLeagueLinks.length ? [{ label: 'Liga Narodów', children: nationsLeagueLinks }] : []),
    { label: 'Liga cen prądu', path: '/pstryk', icon: BoltRoundedIcon },
    ...(archiveSections.length ? [{ label: 'Archiwum', children: archiveSections }] : []),
  ];
};

const isPathActive = (currentPath, targetPath) => {
  if (!targetPath) return false;
  const cleanTargetPath = targetPath.split('?')[0];
  if (cleanTargetPath === '/') return currentPath === '/';
  return currentPath === cleanTargetPath || currentPath.startsWith(`${cleanTargetPath}/`);
};

const NavItem = ({ item, depth = 0, onNavigate }) => {
  const location = useLocation();
  const [open, setOpen] = useState(depth === 0 && item.label === 'Rozgrywki');
  const Icon = item.icon || SportsSoccerRoundedIcon;
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const active = isPathActive(location.pathname, item.path);

  if (hasChildren) {
    return (
      <>
        <ListItemButton
          onClick={() => setOpen((value) => !value)}
          sx={{
            minHeight: depth ? 36 : 44,
            px: 1.5,
            pl: 1.5 + depth * 1.75,
            borderRadius: 1.5,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'rgba(13, 214, 142, 0.08)', color: 'text.primary' },
          }}
        >
          {depth === 0 && (
            <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>
              <Icon fontSize="small" />
            </ListItemIcon>
          )}
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: depth ? '0.84rem' : '0.93rem',
              fontWeight: depth ? 600 : 700,
              noWrap: true,
            }}
          />
          {open ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: depth === 0 ? 1.25 : 0 }}>
            {item.children.map((child) => (
              <NavItem key={`${item.label}-${child.label}-${child.path || ''}`} item={child} depth={depth + 1} onNavigate={onNavigate} />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemButton
      component={RouterLink}
      to={item.path}
      data-analytics-placement="navigation"
      onClick={onNavigate}
      sx={{
        minHeight: depth ? 34 : 44,
        px: 1.5,
        pl: 1.5 + depth * 1.75,
        borderRadius: 1.5,
        color: active ? 'common.white' : 'text.secondary',
        bgcolor: active ? 'rgba(13, 214, 142, 0.16)' : 'transparent',
        border: active ? '1px solid rgba(13, 214, 142, 0.35)' : '1px solid transparent',
        textDecoration: 'none',
        '&:hover': {
          bgcolor: active ? 'rgba(13, 214, 142, 0.22)' : 'rgba(255, 255, 255, 0.06)',
          color: 'common.white',
        },
      }}
    >
      {depth === 0 && (
        <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>
          <Icon fontSize="small" />
        </ListItemIcon>
      )}
      <ListItemText
        primary={item.label}
        primaryTypographyProps={{
          fontSize: depth ? '0.84rem' : '0.93rem',
          fontWeight: active ? 800 : 650,
          noWrap: true,
        }}
      />
    </ListItemButton>
  );
};

const AccountMenu = ({ compact = false, onNavigate }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState('');
  const open = Boolean(anchorEl);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser && currentUser.emailVerified) {
        setUser(currentUser);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        setUserName(userDoc.exists() ? userDoc.data().name || '' : '');
      } else {
        setUser(null);
        setUserName('');
      }
    });

    return () => unsubscribe();
  }, []);

  const label = user ? userName || user.email || 'Konto' : 'Zaloguj';

  const close = () => setAnchorEl(null);
  const go = (path) => {
    close();
    onNavigate?.();
    navigate(path);
  };
  const logout = async () => {
    close();
    await auth.signOut();
    onNavigate?.();
    navigate('/login');
  };

  return (
    <>
      {compact ? (
        <Tooltip title={label}>
          <IconButton onClick={(event) => setAnchorEl(event.currentTarget)} sx={{ color: 'text.primary' }}>
            <AccountCircleRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          onClick={(event) => setAnchorEl(event.currentTarget)}
          fullWidth
          startIcon={<Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(13, 214, 142, 0.22)', color: '#35f0aa' }}>{label.charAt(0).toUpperCase()}</Avatar>}
          endIcon={<ExpandMoreRoundedIcon />}
          sx={{
            justifyContent: 'flex-start',
            color: 'text.primary',
            textTransform: 'none',
            border: '1px solid rgba(255,255,255,0.1)',
            bgcolor: 'rgba(255,255,255,0.04)',
            px: 1.25,
            py: 1,
            borderRadius: 1.5,
          }}
        >
          <Box sx={{ overflow: 'hidden', textAlign: 'left' }}>
            <Typography noWrap sx={{ fontSize: '0.88rem', fontWeight: 800, lineHeight: 1.2 }}>
              {label}
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.2 }}>
              {user ? 'Konto użytkownika' : 'Dostęp do konta'}
            </Typography>
          </Box>
        </Button>
      )}

      <Menu anchorEl={anchorEl} open={open} onClose={close} keepMounted>
        {user ? (
          [
            <MenuItem key="subscription" onClick={() => go('/konto/subskrypcja')}>Subskrypcja</MenuItem>,
            isAdmin && <MenuItem key="admin" onClick={() => go('/janekjot')}>Panel administratora</MenuItem>,
            <MenuItem key="logout" onClick={logout}>
              <ListItemIcon><LogoutRoundedIcon fontSize="small" /></ListItemIcon>
              Wyloguj
            </MenuItem>,
          ].filter(Boolean)
        ) : (
          [
            <MenuItem key="login" onClick={() => go('/login')}>
              <ListItemIcon><LoginRoundedIcon fontSize="small" /></ListItemIcon>
              Logowanie
            </MenuItem>,
            <MenuItem key="register" onClick={() => go('/register')}>Rejestracja</MenuItem>,
          ]
        )}
      </Menu>
    </>
  );
};

const NavigationMenu = ({ onNavigate, mobile = false }) => {
  const { competitions, loading, activeSeason } = useCompetitions();
  const competitionSections = useMemo(
    () => buildCompetitionSections(competitions, activeSeason),
    [competitions, activeSeason]
  );

  const items = [
    ...topLevelItems.slice(0, 1),
    { label: 'Rozgrywki', icon: SportsSoccerRoundedIcon, children: competitionSections },
    ...topLevelItems.slice(1),
    {
      label: 'O projekcie',
      icon: InfoRoundedIcon,
      children: [
        { label: 'FAQ', path: '/blog/faq-najczesciej-zadawane-pytania' },
        { label: 'Jak działają symulacje', path: '/blog/jak-tworzone-sa-symulacje' },
      ],
    },
  ];

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Box
        component={RouterLink}
        to="/"
        onClick={onNavigate}
        sx={{ px: mobile ? 2 : 2.5, py: mobile ? 2 : 2.5, color: 'inherit', textDecoration: 'none', display: 'block' }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            component="img"
            src={logo}
            alt="Symuliga.pl"
            sx={{
              width: 42,
              height: 42,
              objectFit: 'contain',
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.04)',
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.12rem', fontWeight: 900, letterSpacing: 0 }}>
              SYMULIGA.PL
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.74rem', lineHeight: 1.2 }}>
              Polska piłka w liczbach
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      <Box sx={{ px: 1.25, py: 1.5, overflowY: 'auto', flex: 1 }}>
        {loading ? (
          <Stack spacing={1} sx={{ px: 1 }}>
            <Chip label="Ładowanie rozgrywek..." size="small" />
          </Stack>
        ) : (
          <List disablePadding sx={{ display: 'grid', gap: 0.5 }}>
            {items.map((item) => (
              <NavItem key={item.label} item={item} onNavigate={onNavigate} />
            ))}
          </List>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      <Box sx={{ p: 1.5 }}>
        <AccountMenu onNavigate={onNavigate} />
      </Box>
    </Stack>
  );
};

export default NavigationMenu;
export { AccountMenu };
